/**
 * Composable for design-related API calls.
 *
 * Centralizes all fetch calls to the veyo-product-personalizer AJAX backend
 * (ajax.php?type=...) and the WordPress admin-ajax.php endpoint.
 */
import { getActivePinia, type Pinia } from 'pinia';
import { useConfigStore } from '../stores/configStore';
import { useDesignStore, type ViewName } from '../stores/designStore';
import { useProductStore } from '../stores/productStore';
import { useTeamStore } from '../stores/teamStore';
import { useUiStore } from '../stores/uiStore';
import { useCanvasRenderer } from './useCanvasRenderer';
import { useVectorExport } from './useVectorExport';
import { eventBus } from '@core/EventBus';
import {
  getEffectivePrintingPayloadCode,
  parsePrintingCodes,
} from '../utils/printingPayload';
import { resolveViewAreaSizes, resolveViewPhysicalSizesCm } from '../utils/printArea';
import {
  missingRequiredAttributes,
  serializeAttributeSelections,
} from '@shared/productAttributes';
import type { PagedResult } from '@shared/pagination';
import { clampEmbroiderySettings, embroideryViewProfile } from '@core/embroideryProfile';
import { resolvePrintingMethodProviderId } from '@core/extensionRegistry';
import { serializeDesignItems } from './useDesignIO';
import { t } from '@shared/i18n';
import {
  createDesignerRequestId,
  DesignerRequestError,
  publicDesignerError,
  requestDesignerJson,
} from '../services/designerRequest';
import { veyopAjaxUrl, veyopActionUrl } from '@core/veyopRuntime';

let pricingRequestSequence = 0;
let pricingAbortController: AbortController | null = null;
let currencyReloadRequested = false;

// Prevents a double-click (or a stray second trigger) from firing two heavy
// multi-MB save requests at once.
let saveInFlight = false;
let addCartInFlight = false;
let pendingSaveRequestId: string | null = null;
let pendingAddCartRequestId: string | null = null;

export interface DesignLoadResult {
  loaded: boolean;
  sourceUpdatedAt: number;
  reason?: 'not_found' | 'invalid_state';
}

export interface SaveDesignOptions {
  asCopy?: boolean;
}

/**
 * Produce a small JPEG thumbnail dataURL from a rendered (opaque garment-mockup)
 * canvas, so a save request doesn't carry a second full-resolution PNG per view.
 */
function canvasToThumbDataUrl(canvas: HTMLCanvasElement, maxSide = 400): string {
  const w = canvas.width;
  const h = canvas.height;
  const longest = Math.max(w, h);
  const scale = longest > maxSide ? maxSide / longest : 1;
  const small = document.createElement('canvas');
  small.width = Math.max(1, Math.round(w * scale));
  small.height = Math.max(1, Math.round(h * scale));
  const ctx = small.getContext('2d');
  if (!ctx) {
    return canvas.toDataURL('image/jpeg', 0.7);
  }
  // JPEG has no alpha channel. Composite transparent product-canvas pixels onto
  // white explicitly; browser encoders otherwise turn them black.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, small.width, small.height);
  ctx.drawImage(canvas, 0, 0, small.width, small.height);
  return small.toDataURL('image/jpeg', 0.7);
}

export function useDesignApi(pinia?: Pinia) {
  const p = pinia ?? getActivePinia();
  if (!p) {
    throw new Error('useDesignApi: no active Pinia instance');
  }
  const config = useConfigStore(p);
  const design = useDesignStore(p);
  const product = useProductStore(p);
  const ui = useUiStore(p);

  const baseUrl = () => config.data.veyopUrl;

  function buildPreflight() {
    // showStatusPill/targetDpi are owned by the preflight addon (config.items.preflightDefaults,
    // set via veyop_preflight_designer_config); core only reads them, untyped, to gate the bridge.
    const preflightDefaults = (config.data.items as { preflightDefaults?: { showStatusPill?: boolean } }).preflightDefaults;
    if (preflightDefaults?.showStatusPill === false) return null;
    // The preflight addon (paid, optional) supplies the engine; if it's not
    // installed or is disabled there's nothing to gate on.
    const build = window.Veyop?.preflight?.buildReport;
    if (!build) return null;
    const stageWidth = Number(config.data.items.boxWidth) || 500;
    const stageHeight = Number(config.data.items.boxHeight) || 500;
    return build(design.items, {
      stageWidth,
      stageHeight,
      areaSizes: resolveViewAreaSizes(product.product?.areas, stageWidth, stageHeight),
      physicalSizesCm: resolveViewPhysicalSizesCm(product.product?.params),
      profile: config.data.items.preflightProfile,
      activePrintCode: product.selectedPrintingCode,
      activePrintMethodPack: resolvePrintingMethodProviderId(product.selectedPrinting),
      embroideryProfile: config.data.items.embroideryProfile,
      garmentColors: product.colorHexList,
      garmentHasTexture: Boolean(product.activeColor?.textureUrl),
    });
  }

  function payloadPrintingCode(): string {
    return getEffectivePrintingPayloadCode(
      product.selectedPrintingCode,
      product.availablePrintings,
      config.data.printType,
    );
  }

  async function buildEmbroideryProductionManifest(
    renderer = useCanvasRenderer(p),
  ): Promise<Record<string, unknown>> {
    const stageWidth = Number(config.data.items.boxWidth) || 500;
    const stageHeight = Number(config.data.items.boxHeight) || 500;
    const areaSizes = resolveViewAreaSizes(product.product?.areas, stageWidth, stageHeight);
    const physical = resolveViewPhysicalSizesCm(product.product?.params);
    const items: Record<string, unknown>[] = [];
    const printingCode = payloadPrintingCode();
    const isEmbroidery = resolvePrintingMethodProviderId(product.selectedPrinting) === 'embroidery-pack'
      || /embroid/i.test(printingCode);
    if (!isEmbroidery) {
      return { version: 2, printCode: 'embroidery', mode: 'global', items, views: {} };
    }
    for (const [view, layers] of Object.entries(design.items) as [ViewName, typeof design.items[ViewName]][]) {
      const viewProfile = embroideryViewProfile(config.data.items.embroideryProfile, view);
      if (!viewProfile.enabled) continue;
      const area = areaSizes[view] ?? { width: stageWidth, height: stageHeight };
      const sizeCm = physical[view] ?? { width: 0, height: 0 };
      for (const item of layers) {
        const rawSettings = item.embroidery as Record<string, unknown> | undefined;
        const settings = clampEmbroiderySettings(rawSettings, viewProfile);
        if (!['text', 'image', 'svg'].includes(item.type)) continue;
        const widthMm = (Number(item.width) / Math.max(1, area.width)) * sizeCm.width * 10;
        const heightMm = (Number(item.height) / Math.max(1, area.height)) * sizeCm.height * 10;
        const density = Number(settings.density);
        const source = String(item.src ?? item.large ?? item.file ?? '');
        items.push({
          view,
          itemId: item.id,
          type: item.type,
          source: source.startsWith('data:') ? '' : source,
          widthMm: Math.round(widthMm * 100) / 100,
          heightMm: Math.round(heightMm * 100) / 100,
          estimatedStitches: Math.round(widthMm * heightMm * density * 1.8),
          settings: {
            version: 2,
            threadColor: String(settings.threadColor ?? '#1a1a1a'),
            density,
            stitchAngle: Number(settings.stitchAngle),
            textureOverlay: settings.textureOverlay !== false,
            seed: String(settings.seed ?? item.id),
            opaqueBackground: Boolean(settings.opaqueBackground),
          },
          palette: item.type === 'text'
            ? [String(item.color ?? '#000000')]
            : [],
        });
      }
    }
    const views: Record<string, { productionPng: string; sourceSvg: string }> = {};
    for (const view of design.views) {
      if (!design.items[view]?.length) continue;
      const canvas = await renderer.renderForProductionExport(view);
      if (!canvas) continue;
      views[view] = { productionPng: canvas.toDataURL('image/png'), sourceSvg: '' };
    }
    return { version: 2, printCode: 'embroidery', mode: 'global', items, views };
  }

  /**
   * POST JSON to ajax.php (php://input). Prefer this for pricing: multipart FormData
   * sometimes does not populate $_POST under PHP-FPM/nginx, which led to empty responses.
   */
  async function postJson(
    type: string,
    body: Record<string, unknown>,
    signal?: AbortSignal,
    readOnly = false,
  ): Promise<unknown> {
    const requestUrl = veyopAjaxUrl(type, {}, baseUrl());
    return requestDesignerJson(requestUrl, {
      operation: type,
      readOnly,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        ...(config.data.csrfToken ? { 'X-CSRF-Token': config.data.csrfToken } : {}),
      },
      body: JSON.stringify(body),
      signal,
    });
  }

  /** Generic POST helper */
  async function post(
    type: string,
    data?: Record<string, string>,
    options: { readOnly?: boolean; signal?: AbortSignal } = {},
  ): Promise<unknown> {
    const formData = new FormData();
    formData.append('type', type);
    if (data) {
      for (const [key, val] of Object.entries(data)) {
        formData.append(key, val);
      }
    }
    const requestUrl = veyopAjaxUrl(type, {}, baseUrl());
    return requestDesignerJson(requestUrl, {
      operation: type,
      readOnly: options.readOnly,
      method: 'POST',
      body: formData,
      signal: options.signal,
    });
  }

  /** Save the current design (guarded against concurrent invocations). */
  async function saveDesign(title = '', options: SaveDesignOptions = {}): Promise<{ success: boolean; id?: string; url?: string; msg?: string }> {
    if (saveInFlight) {
      return { success: false, msg: t('Saving is already in progress. Please wait…') };
    }
    saveInFlight = true;
    try {
      return await saveDesignInner(title, options);
    } finally {
      saveInFlight = false;
    }
  }

  async function saveDesignInner(title = '', options: SaveDesignOptions = {}): Promise<{ success: boolean; id?: string; url?: string; msg?: string }> {
    const renderer = useCanvasRenderer();
    const vectorExport = useVectorExport();
    const preflight = buildPreflight();
    const embroideryProduction = await buildEmbroideryProductionManifest(renderer);
    const activeCanvas = await renderer.renderForCartExport(design.activeView);
    if (!activeCanvas) {
      return { success: false, msg: 'Add something to the design before saving.' };
    }

    const images: Record<string, string> = {};
    const thumbs: Record<string, string> = {};
    for (const view of design.views) {
      if (!design.items[view]?.length) continue;
      const canvas = await renderer.renderForCartExport(view);
      if (!canvas) continue;
      images[view] = canvas.toDataURL('image/png');
      thumbs[view] = canvasToThumbDataUrl(canvas);
    }

    const requestId = pendingSaveRequestId ?? createDesignerRequestId();
    pendingSaveRequestId = requestId;
    const result = (await postJson('saveDesign', {
      request_id: requestId,
      image: activeCanvas.toDataURL('image/png'),
      images,
      thumbs,
      vectors: await vectorExport.exportVectors(),
      fonts: vectorExport.extractFonts(),
      teams: '',
      cliparts: JSON.stringify(vectorExport.extractCliparts()),
      colors: JSON.stringify(vectorExport.extractPrintColors()),
      print: JSON.stringify(vectorExport.extractPrintSizes()),
      title,
      description: '',
      product_id: config.data.productId,
      parent_id: config.data.parentId,
      product_color: product.activeColor?.hex ?? config.data.initialColor ?? '',
      print_type: payloadPrintingCode(),
      designer_id: options.asCopy ? '' : (design.designerId || ''),
      design_file: options.asCopy ? '' : (design.designFile || ''),
      design_key: options.asCopy ? '' : (design.designKey || ''),
      designer_state: {
        items: serializeDesignItems(design.items),
        activeView: design.activeView,
        views: design.views,
        selectedAttributes: product.selectedAttributes,
      },
      attribute: serializeAttributeSelections(product.product?.attributes ?? [], product.selectedAttributes),
      preflight,
      production: { embroidery: embroideryProduction },
    })) as { error?: number | string; content?: Record<string, unknown>; msg?: string };

    const success = Number(result.error ?? 1) === 0;
    pendingSaveRequestId = null;
    const id = String(result.content?.design_id ?? result.content?.design_key ?? '');
    if (success && id) {
      design.designId = id;
      design.designKey = String(result.content?.design_key ?? id);
      design.designerId = String(result.content?.designer_id ?? '');
      design.designFile = String(result.content?.design_file ?? '');
      design.origin = 'saved';
      eventBus.emit('after.save.design' as string, { success, id });
    }
    return { success, id, msg: result.msg };
  }

  /** Load a saved design by ID */
  async function loadDesign(designId: string, designerId = ''): Promise<DesignLoadResult> {
    let data: { error?: number | string; design?: Record<string, unknown>; msg?: string };
    try {
      data = (await post('myDesignLoad', {
        design_id: designId,
        designer_id: designerId,
        share_token: config.data.shareToken || '',
      }, { readOnly: true })) as typeof data;
    } catch (err) {
      console.error('[loadDesign] Network error:', err);
      throw new Error(publicDesignerError(err, 'Failed to load design. Please check your connection and try again.'));
    }

    if (Number(data?.error ?? 1) === 0 && data.design) {
      const saved = data.design;

      const savedCodes = parsePrintingCodes(String(saved.print_type ?? ''));
      if (savedCodes.length) {
        product.setSelectedPrintingCode(savedCodes[0]);
      }

      const state = saved.designer_state as {
        items?: Partial<Record<ViewName, import('@core/types').DesignItem[]>>;
        activeView?: string;
        views?: ViewName[];
        selectedAttributes?: import('@shared/productAttributes').AttributeSelections;
      } | undefined;
      if (state?.items) {
        eventBus.emit('before.load.design' as string, saved);
        if (Array.isArray(state.views) && state.views.length) {
          design.setAvailableViews(state.views);
        }
        design.loadDesignState({
          items: serializeDesignItems(state.items),
          activeView: state.activeView,
          designId,
          designKey: String(saved.design_key ?? saved.design_id ?? designId),
          designerId: String(saved.user_id ?? ''),
          designFile: String(saved.image ?? ''),
          origin: config.data.shareToken ? 'shared' : 'saved',
        });
        if (state.selectedAttributes) product.setAttributes(state.selectedAttributes);
        const sourceUpdatedAt = Date.parse(String(saved.updated_at ?? saved.created_at ?? '')) || 0;
        return { loaded: true, sourceUpdatedAt };
      }
    }
    return { loaded: false, sourceUpdatedAt: 0, reason: 'not_found' };
  }

  /** Load a cart/order design by its opaque cart cache key. */
  async function loadCartDesign(cartId: string): Promise<DesignLoadResult> {
    let data: { error?: number | string; design?: Record<string, unknown>; msg?: string };
    try {
      data = (await post('cartDesign', {
        cart_id: cartId,
        order_item_id: String(config.data.orderItemId || ''),
        order_access: String(config.data.orderAccess || ''),
      }, { readOnly: true })) as typeof data;
    } catch (err) {
      console.error('[loadCartDesign] Network error:', err);
      throw new Error(publicDesignerError(err, 'Failed to load this cart design.'));
    }
    if (Number(data?.error ?? 1) !== 0 || !data.design) {
      return { loaded: false, sourceUpdatedAt: 0, reason: 'not_found' };
    }
    const saved = data.design;
    const state = saved.designer_state as {
      items?: Partial<Record<ViewName, import('@core/types').DesignItem[]>>;
      activeView?: string;
      views?: ViewName[];
      selectedAttributes?: import('@shared/productAttributes').AttributeSelections;
    } | undefined;
    if (!state?.items) return { loaded: false, sourceUpdatedAt: 0, reason: 'invalid_state' };
    const printCode = String((saved.printing as { code?: string } | undefined)?.code ?? saved.print_type ?? '');
    if (printCode) product.setSelectedPrintingCode(printCode);
    eventBus.emit('before.load.design' as string, saved);
    if (Array.isArray(state.views) && state.views.length) design.setAvailableViews(state.views);
    design.loadDesignState({
      items: serializeDesignItems(state.items),
      activeView: state.activeView,
      designId: cartId,
      designKey: cartId,
      designerId: '',
      designFile: '',
      origin: 'cart',
    });
    if (state.selectedAttributes) product.setAttributes(state.selectedAttributes);
    const isEmbroidery = resolvePrintingMethodProviderId(product.selectedPrinting) === 'embroidery-pack'
      || /embroid/i.test(printCode);
    if (Number(config.data.orderItemId) > 0 && isEmbroidery) {
      try {
        const manifest = await buildEmbroideryProductionManifest();
        await post('materializeProductionAsset', {
          cart_id: cartId,
          manifest: JSON.stringify(manifest),
        });
      } catch (err) {
        // Customers can load their own order design but only shop staff may
        // materialize production files. A rejected background request must not
        // block the editable design.
        console.debug('[loadCartDesign] Production materialization skipped', err);
      }
    }
    const sourceUpdatedAt = Date.parse(String(saved.updated_at ?? saved.created_at ?? '')) || 0;
    return { loaded: true, sourceUpdatedAt };
  }

  /**
   * Recalculate price via dg::prices (JSON POST body → CartHandler jsonInput).
   */
  async function calculateDesignerPrice(
    fields: Record<string, unknown>,
  ): Promise<{ price: number; regularPrice: number }> {
    if (config.data.mode === 'template-admin') {
      return { price: product.price, regularPrice: product.oldPrice || product.price };
    }
    pricingAbortController?.abort();
    pricingAbortController = new AbortController();
    const requestSequence = ++pricingRequestSequence;

    let result: {
      price?: number;
      regular_price?: number;
      error?: unknown;
      msg?: string;
      display?: { includes_tax?: boolean; suffix?: string };
      violations?: Array<{ message?: string }>;
      breakdown?: import('../stores/productStore').PriceBreakdown;
      currency?: import('../../shared/money').CurrencyContext;
    };
    try {
      product.setPriceStatus('loading');
      result = (await postJson('prices', fields, pricingAbortController.signal, true)) as typeof result;
    } catch (err) {
      if ((err instanceof DesignerRequestError && err.code === 'aborted') || requestSequence !== pricingRequestSequence) {
        return { price: product.price, regularPrice: product.oldPrice || product.price };
      }
      if (err instanceof DesignerRequestError && err.status === 409 && !currencyReloadRequested) {
        currencyReloadRequested = true;
        window.location.reload();
        return { price: product.price, regularPrice: product.oldPrice || product.price };
      }
      console.error('[calculateDesignerPrice] Network error:', err);
      product.setPriceStatus('unavailable');
      return { price: product.price, regularPrice: product.oldPrice || product.price };
    }

    if (requestSequence !== pricingRequestSequence) {
      return { price: product.price, regularPrice: product.oldPrice || product.price };
    }

    // Display-only fallback: when the server cannot quote (error or 0), show the
    // catalog/WC base line so the UI never reads $0. The authoritative price —
    // including printing tiers and personalization fees — is always computed by
    // the server (PricingService); the client no longer duplicates that math.
    const applyDisplayFallbackLine = (): { price: number; regularPrice: number } | null => {
      const q = Math.max(1, Math.round(Number(product.quantity) || 1));
      const wc = product.wcProduct;
      const pd = product.product;
      let unitSale = 0;
      let unitReg = 0;
      if (wc) {
        const rp = Number(wc.regularPrice) || 0;
        const sp = Number(wc.salePrice) || 0;
        if (sp > 0 && rp > sp) {
          unitSale = sp;
          unitReg = rp;
        } else if (rp > 0) {
          unitSale = rp;
          unitReg = rp;
        } else if (sp > 0) {
          unitSale = sp;
          unitReg = sp;
        }
      }
      if (unitSale <= 0 && pd) {
        const rp = Number(pd.regularPrice) || 0;
        const sp = Number(pd.salePrice) || 0;
        const bp = Number(pd.price) || 0;
        if (sp > 0 && rp > sp) {
          unitSale = sp;
          unitReg = rp;
        } else if (bp > 0) {
          unitSale = bp;
          unitReg = rp > bp ? rp : bp;
        } else if (rp > 0) {
          unitSale = rp;
          unitReg = rp;
        } else if (sp > 0) {
          unitSale = sp;
          unitReg = sp;
        }
      }
      if (unitSale <= 0) {
        return null;
      }
      const lineSale = unitSale * q;
      const lineReg = unitReg * q;
      const oldArg = lineReg > lineSale ? lineReg : lineSale;
      product.setPrice(lineSale, oldArg);
      return { price: lineSale, regularPrice: lineReg };
    };

    if (result && typeof result === 'object' && result.error) {
      console.warn('[calculateDesignerPrice]', result.msg ?? result.error);
      product.setPriceStatus('unavailable', typeof result.msg === 'string' ? result.msg : '');
      const fbErr = applyDisplayFallbackLine();
      if (fbErr) {
        return fbErr;
      }
      return { price: product.price, regularPrice: product.oldPrice || product.price };
    }

    const price = Number(result.price ?? 0);
    const regularPrice = Number(result.regular_price ?? price);

    // Legacy dg::prices uses catalog rows; WC storefront price may live only on parent.
    // Avoid replacing a good config/WC display with 0 from server.
    if (price <= 0) {
      product.setPriceStatus('unavailable');
      const fbZero = applyDisplayFallbackLine();
      if (fbZero) {
        return fbZero;
      }
    }

    // Mirror the server's tax-display metadata + itemized breakdown.
    product.setPriceMeta({
      suffix: String(result.display?.suffix ?? ''),
      includesTax: Boolean(result.display?.includes_tax),
      breakdown: result.breakdown ?? null,
    });
    if (result.currency) {
      config.data.currency = result.currency;
      config.data.currencySymbol = result.currency.symbol;
      config.data.currencyPosition = result.currency.position;
    }

    product.setPrice(price, regularPrice);
    // Priced, but not orderable as is (e.g. below a minimum quantity): say so before add-to-cart refuses it.
    product.setPriceStatus('ready', String(result.violations?.[0]?.message ?? ''));
    return { price, regularPrice };
  }

  /**
   * Add current design to WooCommerce cart.
   * `reason: 'preflight'` = stopped on the artwork check (the preflight panel was expanded),
   * so a caller showing a modal should close it to reveal the panel.
   */
  async function addToCart(): Promise<{ success: boolean; redirect?: string; reason?: 'preflight' }> {
    if (addCartInFlight || ui.addingToCart) return { success: false };
    const missingAttributes = missingRequiredAttributes(
      product.product?.attributes ?? [],
      product.selectedAttributes,
    );
    if (missingAttributes.length) {
      product.showAttributeErrors();
      ui.openLeftPanel('product');
      ui.alert(`Choose ${missingAttributes.map((attr) => attr.name).join(', ')} before adding to cart.`);
      return { success: false };
    }
    const gateReport = buildPreflight();
    if (gateReport && gateReport.status !== 'pass' && gateReport.status !== 'idle') {
      if (gateReport.enforcement === 'block' && gateReport.status === 'error') {
        eventBus.emit('veyop:preflight:expand-request', undefined);
        ui.alert(t('Fix print-readiness errors before adding this design to cart.'));
        return { success: false, reason: 'preflight' };
      }
    }

    // Lock before any await so a second trigger cannot start another mutation.
    // UI overlay (addingToCart) is set only after optional warn confirm.
    addCartInFlight = true;
    try {
      if (
        gateReport
        && gateReport.status !== 'pass'
        && gateReport.status !== 'idle'
        && gateReport.enforcement === 'warn'
      ) {
        const proceed = await ui.askConfirm(
          `Artwork check found ${gateReport.issues.length} issue(s). Continue with this design?`,
        );
        if (!proceed) {
          eventBus.emit('veyop:preflight:expand-request', undefined);
          return { success: false, reason: 'preflight' };
        }
      }

      ui.setAddingToCart(true);
      eventBus.emit('add.to.cart.design' as string, undefined);

      const team = useTeamStore(p);
      const renderer = useCanvasRenderer(p);
      const vectorExport = useVectorExport(p);
      const preflight = buildPreflight();
      const embroideryProduction = await buildEmbroideryProductionManifest(renderer);

      // Build nested FormData matching legacy jQuery { data: datas } format
      const formData = new FormData();
      const requestId = pendingAddCartRequestId ?? createDesignerRequestId();
      pendingAddCartRequestId = requestId;
      formData.append('action', 'veyop_add_to_cart');
      formData.append('data[request_id]', requestId);
      formData.append('data[expected_currency]', config.data.currency.code);
      if (config.data.csrfToken) {
        formData.append('_token', config.data.csrfToken);
      }

      // Core product data
      formData.append('data[product_id]', config.data.productId);
      formData.append('data[parent_id]', String(config.data.parentId));
      formData.append('data[quantity]', String(product.quantity));
      const quickPersonalization = (config.data as typeof config.data & {
        quickPersonalization?: {
          template_id?: number;
          template_updated_at?: string;
          selections?: Record<string, unknown>;
          options?: Array<Record<string, unknown>>;
        };
      }).quickPersonalization;
      if (quickPersonalization) {
        formData.append('data[template_id]', String(quickPersonalization.template_id || 0));
        formData.append('data[template_updated_at]', String(quickPersonalization.template_updated_at || ''));
        formData.append('data[quick_selections]', JSON.stringify(quickPersonalization.selections || {}));
        formData.append('data[options]', JSON.stringify(quickPersonalization.options || []));
      }
      if (config.data.pricingContext && Object.keys(config.data.pricingContext).length) {
        formData.append('data[pricing_context]', JSON.stringify(config.data.pricingContext));
      }
      if (preflight) {
        formData.append('data[preflight]', JSON.stringify(preflight));
      }

      // Active color (custom color tint persists via tintHex).
      if (product.activeColor || product.customTintHex) {
        formData.append(`data[colors][${product.activeColorIndex}]`, product.tintHex);
      }

      // Variation data
      if (config.data.variationId) {
        formData.append('data[variation_id]', config.data.variationId);
      }
      if (config.data.variationAttributes) {
        formData.append('data[variation_attributes]', config.data.variationAttributes);
      }

      const attributes = serializeAttributeSelections(product.product?.attributes ?? [], product.selectedAttributes);
      for (const [attributeIndex, value] of Object.entries(attributes)) {
        if (typeof value === 'string') {
          formData.append(`data[attribute][${attributeIndex}]`, value);
        } else {
          for (const [optionIndex, optionValue] of Object.entries(value)) {
            formData.append(`data[attribute][${attributeIndex}][${optionIndex}]`, String(optionValue));
          }
        }
      }

      // Print data (sizes + colors)
      const printSizes = vectorExport.extractPrintSizes();
      const printColors = vectorExport.extractPrintColors();
      formData.append('data[print][sizes]', JSON.stringify(printSizes));
      formData.append('data[print][colors]', JSON.stringify(printColors));
      const printCode = payloadPrintingCode();
      formData.append('data[print][type]', printCode);
      formData.append('data[print_type]', printCode);
      formData.append('data[production][embroidery]', JSON.stringify(embroideryProduction));

      // Design vectors (JSON string)
      const vectors = await vectorExport.exportVectors();
      formData.append('data[design][vectors]', JSON.stringify(vectors));
      formData.append('data[designer_state]', JSON.stringify({
        items: serializeDesignItems(design.items),
        views: design.views,
        activeView: design.activeView,
        selectedAttributes: product.selectedAttributes,
      }));

      // Design images — render each view to canvas and get DataURL
      for (const view of design.views) {
        const items = design.items[view];
        if (!items || items.length === 0) continue;

        const canvas = await renderer.renderForCartExport(view);
        if (canvas) {
          formData.append(`data[design][images][${view}]`, canvas.toDataURL('image/png'));
        }
      }

      // Cliparts
      const cliparts = vectorExport.extractCliparts();
      for (const [view, ids] of Object.entries(cliparts)) {
        for (let i = 0; i < ids.length; i++) {
          formData.append(`data[cliparts][${view}][${i}]`, ids[i]);
        }
      }

      // Teams
      if (team.isTeamMode && team.members.length > 0) {
        formData.append('data[teams]', JSON.stringify(team.members));
      }

      // Fonts
      const fonts = vectorExport.extractFonts();
      if (fonts) {
        formData.append('data[fonts]', fonts);
      }

      // WordPress admin-ajax.php?action=…, or the native host's action route.
      const rawResult = await requestDesignerJson<Record<string, unknown>>(
        veyopActionUrl('veyop_add_to_cart', config.data.siteUrl),
        {
          operation: 'addToCart',
          requestId,
          method: 'POST',
          body: formData,
          validate: (payload) => payload !== null && typeof payload === 'object' && 'error' in payload,
        },
      );

      const success = rawResult.error === 0 || rawResult.error === '0';
      pendingAddCartRequestId = null;
      if (!success) {
        if (rawResult.preflight) eventBus.emit('veyop:preflight:expand-request', undefined);
        ui.alert(String(rawResult.msg || 'The cart rejected this design. Please review it and try again.'));
      }
      const redirect = success
        ? String(rawResult.redirect ?? ((window as unknown as { woo_url_cart?: string }).woo_url_cart || `${config.data.siteUrl}cart/`))
        : undefined;

      eventBus.emit('after.add.to.cart.design' as string, { success });

      return { success, redirect };
    } catch (error) {
      if (error instanceof DesignerRequestError && error.status === 409 && !currencyReloadRequested) {
        currencyReloadRequested = true;
        window.location.reload();
        return { success: false };
      }
      ui.alert(publicDesignerError(error, 'Could not add this design to cart.'));
      eventBus.emit('after.add.to.cart.design' as string, { success: false, error });
      return { success: false };
    } finally {
      addCartInFlight = false;
      ui.setAddingToCart(false);
    }
  }

  /** Load user's saved designs list */
  async function getMyDesigns<T = unknown>(page = 1, perPage = 12): Promise<PagedResult<T>> {
    const result = (await post('myDesigns', {
      page: String(page),
      per_page: String(perPage),
    }, { readOnly: true })) as { designs?: T[]; items?: T[]; total?: number; page?: number; per_page?: number; pages?: number; has_more?: boolean };
    const items = Array.isArray(result.items) ? result.items : Array.isArray(result.designs) ? result.designs : [];
    const resolvedPage = Math.max(1, Number(result.page) || page);
    const pages = Math.max(0, Number(result.pages) || Math.ceil((Number(result.total) || items.length) / perPage));
    return {
      items,
      total: Number(result.total) || items.length,
      page: resolvedPage,
      per_page: Number(result.per_page) || perPage,
      pages,
      has_more: typeof result.has_more === 'boolean' ? result.has_more : resolvedPage < pages,
    };
  }

  /** Fetch full WooCommerce product info (uses WC post ID = parentId) */
  async function getProductInfo(productId?: string): Promise<unknown> {
    const id = productId ?? (config.data.parentId ? String(config.data.parentId) : config.data.productId);
    return post('product_info', { product_id: id }, { readOnly: true });
  }

  /** Fetch dynamic price for a given quantity (uses WC post ID = parentId) */
  async function getProductPrice(productId?: string, qty?: number): Promise<{ price: number; regularPrice: number }> {
    const id = productId ?? (config.data.parentId ? String(config.data.parentId) : config.data.productId);
    const q = qty ?? product.quantity;
    const result = (await post('product_price', {
      product_id: id,
      quantity: String(q),
      expected_currency: config.data.currency.code,
    }, { readOnly: true })) as { price?: number; regular_price?: number };
    const price = result.price ?? 0;
    const regularPrice = result.regular_price ?? price;
    return { price, regularPrice };
  }

  /** Fetch linked products from the catalog */
  async function getLinkedProducts(): Promise<unknown[]> {
    const result = (await post('linked_products', {
      product_id: config.data.productId,
    }, { readOnly: true })) as unknown[];
    return Array.isArray(result) ? result : [];
  }

  return {
    post,
    postJson,
    saveDesign,
    loadDesign,
    loadCartDesign,
    calculateDesignerPrice,
    addToCart,
    getMyDesigns,
    getProductInfo,
    getProductPrice,
    getLinkedProducts,
  };
}
